import { jsx, jsxs } from "react/jsx-runtime";
import { motion } from "motion/react";
const steps = [
  { icon: "\u{1F4F7}", label: "Camera", desc: "Live CCTV feed captured" },
  { icon: "\u{1F4E1}", label: "Live Video", desc: "Stream sent to AI engine" },
  { icon: "\u{1F9E0}", label: "AI Vision", desc: "Deep learning model processes" },
  { icon: "\u{1F50D}", label: "Detection", desc: "Objects & persons identified" },
  { icon: "\u{1F464}", label: "Recognition", desc: "Faces & plates matched to DB" },
  { icon: "\u{1F4CA}", label: "Analytics", desc: "Patterns & heatmaps generated" },
  { icon: "\u{1F5A5}\uFE0F", label: "Dashboard", desc: "Real-time data displayed" },
  { icon: "\u{1F6A8}", label: "Alert", desc: "Instant notification triggered" },
  { icon: "\u26A1", label: "Action", desc: "Security team responds" }
];
const detections = [
  { label: "Person Detected", color: "#6f5cf7", x: "14%", y: "28%" },
  { label: "Vehicle Detected", color: "#0ea5e9", x: "62%", y: "18%" },
  { label: "Intrusion Alert", color: "#ef4444", x: "38%", y: "55%" }
];
function SurveillanceFlow() {
  return /* @__PURE__ */ jsx("section", { className: "py-24 px-6", style: { backgroundColor: "#0a0a14" }, children: /* @__PURE__ */ jsxs("div", { className: "max-w-7xl mx-auto", children: [
    /* @__PURE__ */ jsxs("div", { className: "text-center mb-16", children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          className: "inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 text-xs font-semibold",
          style: { backgroundColor: "rgba(111,92,247,0.15)", border: "1px solid rgba(111,92,247,0.3)", color: "#a78bfa", fontFamily: "'Manrope',sans-serif", letterSpacing: "0.06em" },
          children: "AI SURVEILLANCE"
        }
      ),
      /* @__PURE__ */ jsx("h2", { style: { fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(1.8rem,3.5vw,2.75rem)", fontWeight: 800, color: "white", letterSpacing: "-0.02em", marginBottom: "0.75rem" }, children: "AI Surveillance Workflow" }),
      /* @__PURE__ */ jsx("p", { style: { fontFamily: "'Inter',sans-serif", fontSize: "1rem", color: "rgba(255,255,255,0.55)", maxWidth: "480px", margin: "0 auto" }, children: "From raw camera footage to actionable security intelligence \u2014 in milliseconds." })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "relative flex flex-wrap justify-center gap-0 mb-16", children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          className: "hidden md:block absolute top-7 left-[8%] right-[8%] h-px",
          style: { background: "linear-gradient(90deg,transparent,rgba(111,92,247,0.6) 10%,rgba(168,85,247,0.8) 50%,rgba(111,92,247,0.6) 90%,transparent)" }
        }
      ),
      steps.map((s, i) => /* @__PURE__ */ jsxs(
        motion.div,
        {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.5, delay: i * 0.07 },
          className: "flex flex-col items-center text-center relative",
          style: { width: "11.1%", minWidth: "80px", padding: "0 8px" },
          children: [
            /* @__PURE__ */ jsxs(
              "div",
              {
                className: "w-14 h-14 rounded-2xl flex items-center justify-center mb-3 relative z-10",
                style: {
                  background: i === 4 || i === 7 ? "linear-gradient(135deg,#ef4444,#dc2626)" : i === 2 || i === 5 ? "linear-gradient(135deg,#6f5cf7,#8b5cf6)" : "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  boxShadow: i === 7 ? "0 0 20px rgba(239,68,68,0.4)" : i === 2 ? "0 0 16px rgba(111,92,247,0.4)" : "none"
                },
                children: [
                  /* @__PURE__ */ jsx("span", { style: { fontSize: "1.4rem" }, children: s.icon }),
                  i === 7 && /* @__PURE__ */ jsx("span", { className: "absolute inset-0 rounded-2xl animate-ping opacity-20 bg-red-500" })
                ]
              }
            ),
            /* @__PURE__ */ jsx("div", { style: { fontFamily: "'Manrope',sans-serif", fontWeight: 700, fontSize: "0.72rem", color: "white", marginBottom: "0.25rem" }, children: s.label }),
            /* @__PURE__ */ jsx("div", { style: { fontFamily: "'Inter',sans-serif", fontSize: "0.65rem", color: "rgba(255,255,255,0.4)", lineHeight: 1.4 }, children: s.desc })
          ]
        },
        s.label
      ))
    ] }),
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: 32 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.7 },
        className: "relative rounded-3xl overflow-hidden mx-auto",
        style: { maxWidth: "860px", border: "1px solid rgba(255,255,255,0.06)" },
        children: [
          /* @__PURE__ */ jsx(
            "img",
            {
              src: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=1200&h=600&fit=crop&auto=format",
              alt: "AI surveillance camera view of city",
              className: "w-full object-cover",
              style: { height: "340px", filter: "brightness(0.5) saturate(0.7)" }
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "absolute inset-0 p-5", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between mb-4", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsx("span", { className: "w-2 h-2 rounded-full bg-red-500 animate-pulse" }),
                /* @__PURE__ */ jsx("span", { style: { fontFamily: "'Manrope',monospace", fontSize: "0.75rem", color: "rgba(255,255,255,0.8)" }, children: "LIVE \xB7 CAM-04" })
              ] }),
              /* @__PURE__ */ jsx("div", { style: { fontFamily: "'Manrope',monospace", fontSize: "0.72rem", color: "rgba(255,255,255,0.5)" }, children: "AI ACTIVE \xB7 60FPS \xB7 4K" })
            ] }),
            detections.map((d) => /* @__PURE__ */ jsxs(
              "div",
              {
                className: "absolute flex flex-col items-start",
                style: { left: d.x, top: d.y },
                children: [
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: "w-20 h-28 rounded-lg",
                      style: { border: `2px solid ${d.color}`, boxShadow: `0 0 12px ${d.color}50`, backgroundColor: `${d.color}08` }
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: "mt-1 px-2 py-0.5 rounded text-[9px] font-bold",
                      style: { backgroundColor: d.color, color: "white", fontFamily: "'Manrope',sans-serif" },
                      children: d.label
                    }
                  )
                ]
              },
              d.label
            )),
            /* @__PURE__ */ jsx("div", { className: "absolute bottom-4 right-4 flex flex-col gap-2", children: [
              { label: "Persons", value: "14", color: "#6f5cf7" },
              { label: "Vehicles", value: "7", color: "#0ea5e9" },
              { label: "Alerts", value: "2", color: "#ef4444" }
            ].map((w) => /* @__PURE__ */ jsxs(
              "div",
              {
                className: "flex items-center justify-between gap-4 px-3 py-2 rounded-xl",
                style: { backgroundColor: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)", border: `1px solid ${w.color}30`, minWidth: "140px" },
                children: [
                  /* @__PURE__ */ jsxs("span", { style: { fontFamily: "'Inter',sans-serif", fontSize: "0.72rem", color: "rgba(255,255,255,0.6)" }, children: [
                    w.label,
                    " Detected"
                  ] }),
                  /* @__PURE__ */ jsx("span", { style: { fontFamily: "'Manrope',sans-serif", fontWeight: 700, fontSize: "0.95rem", color: w.color }, children: w.value })
                ]
              },
              w.label
            )) }),
            ["top-left", "top-right", "bottom-left", "bottom-right"].map((pos) => {
              const [v, h] = pos.split("-");
              return /* @__PURE__ */ jsx("div", { className: `absolute ${v}-3 ${h}-3`, children: /* @__PURE__ */ jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", children: [
                /* @__PURE__ */ jsx("path", { d: v === "top" ? "M0 6V0h6" : "M0 10v6h6", stroke: "rgba(111,92,247,0.6)", strokeWidth: "1.5", fill: "none" }),
                /* @__PURE__ */ jsx("path", { d: v === "top" ? "M10 0h6v6" : "M10 16h6v-6", stroke: "rgba(111,92,247,0.6)", strokeWidth: "1.5", fill: "none" })
              ] }) }, pos);
            })
          ] })
        ]
      }
    )
  ] }) });
}
export {
  SurveillanceFlow
};
